import { useMemo } from "react";

import { HistoricalPrincipalCollateralBorrowRate, usePrincipalCollateralBorrowRate } from "@bridgesplit/abf-react";
import { BRAND_COLORS, FormatType, TimeseriesChart } from "@bridgesplit/ui";
import { roundTimestamp } from "@bridgesplit/utils";

import { useLoopContext } from "../../LoopContext";
import { OpenChart } from "./chartOptions";

export default function LoopPrincipalDepositHistory({
    openChart,
    period,
    setPeriod,
    xDomain
}: {
    openChart: OpenChart;
    period: number;
    setPeriod: (p: number) => void;
    xDomain: [number, number];
}) {
    const historyData = useLoopPrincipalCollateralHistoryData(period);
    const isBorrows = openChart === OpenChart.Borrows;
    const valueKey = isBorrows ? ("totalPrincipalDeployedUsd" as const) : ("collateralDepositedUsd" as const);
    const color = isBorrows ? BRAND_COLORS.BLUE : BRAND_COLORS.ORANGE;
    const label = isBorrows ? "Borrows" : "Collateral";

    const chartData = useMemo<LoopPrincipalCollateralChartPoint[]>(
        () =>
            historyData?.map((point) => ({
                ...point,
                chartValue: point[valueKey] ?? 0
            })) ?? [],
        [historyData, valueKey]
    );

    return (
        <TimeseriesChart<LoopPrincipalCollateralChartPoint>
            chartId={`loop-principal-${openChart}`}
            data={chartData}
            height={200}
            axisFormat={FormatType.Usd}
            series={[{ dataKey: "chartValue", label, color }]}
            hideLegend
            xDomain={xDomain}
            period={period}
            setPeriod={setPeriod}
        />
    );
}

function useLoopPrincipalCollateralHistoryData(period: number) {
    const { loopExpanded } = useLoopContext();
    const collateralMint = loopExpanded?.collateralToken.mint;
    const { data: dataRecord } = usePrincipalCollateralBorrowRate(
        {
            principalMint: loopExpanded?.principalToken.mint,
            collateralMints: collateralMint ? [collateralMint] : undefined,
            timeLookback: period
        },
        { skip: !loopExpanded || !collateralMint }
    );
    const data = collateralMint ? dataRecord?.perCollateral?.[collateralMint] : undefined;
    return useMemo(() => {
        if (!data?.length || !loopExpanded) return undefined;

        const sorted = [...data].sort((a, b) => a.timestamp - b.timestamp);
        const seenTimestamps = new Set<number>();
        const chartData: LoopPrincipalCollateralHistoryPoint[] = [];

        for (const row of sorted) {
            const roundedTimestamp = roundTimestamp(row.timestamp);
            if (seenTimestamps.has(roundedTimestamp)) continue;
            seenTimestamps.add(roundedTimestamp);

            chartData.push({
                ...row,
                date: roundedTimestamp
            });
        }

        return chartData;
    }, [data, loopExpanded]);
}

export type LoopPrincipalCollateralHistoryPoint = HistoricalPrincipalCollateralBorrowRate & {
    date: number;
};
type LoopPrincipalCollateralChartPoint = LoopPrincipalCollateralHistoryPoint & {
    chartValue: number;
};
